import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Send } from "lucide-react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ROOMS } from "../lib/site";

// Static-site form endpoint — FormSubmit sends the form data to your email inbox.
// First submission triggers a one-time verification email to havelibb@gmail.com.
// Click the link inside it once; all future bookings arrive directly in your inbox.
const FORM_ENDPOINT = "https://formsubmit.co/ajax/havelibb@gmail.com";

const inputCls =
  "w-full bg-transparent border-b border-ink/20 focus:border-terracotta outline-none py-3 text-ink placeholder:text-ink-soft/60 text-sm transition-colors duration-300";

export default function BookingForm() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", guests: 2, room_type: "Standard Room", message: "",
  });
  const [checkIn, setCheckIn] = useState();
  const [checkOut, setCheckOut] = useState();
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!checkIn || !checkOut) { toast.error("Please pick your check-in and check-out dates."); return; }
    if (checkOut <= checkIn) { toast.error("Check-out must be after check-in."); return; }
    if (!form.name || !form.email || !form.phone) { toast.error("Please fill in your name, email and phone."); return; }

    setLoading(true);
    try {
      const payload = {
        _subject: `New booking enquiry — ${form.name} (${form.room_type})`,
        _template: "table",
        _captcha: "false",
        _autoresponse: `Namaste ${form.name},\n\nThank you for your booking enquiry at Haveli Braj Bhushanjee. A member of the Bohara Braj Bhushanjee family will reply personally within 24 hours to confirm availability.\n\nYour request:\n• Check-in: ${format(checkIn, "yyyy-MM-dd")}\n• Check-out: ${format(checkOut, "yyyy-MM-dd")}\n• Guests: ${form.guests}\n• Room: ${form.room_type}\n\nDirect contact:\nWhatsApp / Phone: +91 9783355866\nEmail: havelibb@gmail.com\n\n— Haveli Braj Bhushanjee, Bundi`,
        Name: form.name,
        Email: form.email,
        Phone: form.phone,
        "Check-in": format(checkIn, "yyyy-MM-dd"),
        "Check-out": format(checkOut, "yyyy-MM-dd"),
        Guests: Number(form.guests),
        Room: form.room_type,
        Message: form.message || "(no message)",
      };
      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Send failed");
      toast.success("Enquiry sent. We will confirm within 24 hours.", { duration: 6000 });
      setForm({ name: "", email: "", phone: "", guests: 2, room_type: "Standard Room", message: "" });
      setCheckIn(undefined); setCheckOut(undefined);
    } catch (err) {
      toast.error("Could not send. Please try WhatsApp +91 9783355866 or email havelibb@gmail.com.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="booking-form" className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="overline text-ink-soft mb-2 block">Full name</label>
          <input data-testid="input-name" type="text" required value={form.name} onChange={update("name")} placeholder="Your name" className={inputCls}/>
        </div>
        <div>
          <label className="overline text-ink-soft mb-2 block">Email</label>
          <input data-testid="input-email" type="email" required value={form.email} onChange={update("email")} placeholder="you@example.com" className={inputCls}/>
        </div>
        <div>
          <label className="overline text-ink-soft mb-2 block">Phone / WhatsApp</label>
          <input data-testid="input-phone" type="tel" required value={form.phone} onChange={update("phone")} placeholder="+91 ..." className={inputCls}/>
        </div>
        <div>
          <label className="overline text-ink-soft mb-2 block">Guests</label>
          <input data-testid="input-guests" type="number" min={1} max={10} required value={form.guests} onChange={update("guests")} className={inputCls}/>
        </div>
      </div>

      <div>
        <label className="overline text-ink-soft mb-2 block">Check-in</label>
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" data-testid="btn-checkin" className={`${inputCls} text-left flex items-center justify-between`}>
              <span>{checkIn ? format(checkIn, "EEE, d MMM yyyy") : "Select date"}</span>
              <CalendarIcon size={16} className="text-ink-soft"/>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-surface border border-[#E5E1D8]" align="start">
            <Calendar mode="single" selected={checkIn} onSelect={setCheckIn} defaultMonth={checkIn || new Date()} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} initialFocus/>
          </PopoverContent>
        </Popover>
      </div>

      <div>
        <label className="overline text-ink-soft mb-2 block">Check-out</label>
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" data-testid="btn-checkout" className={`${inputCls} text-left flex items-center justify-between`}>
              <span>{checkOut ? format(checkOut, "EEE, d MMM yyyy") : "Select date"}</span>
              <CalendarIcon size={16} className="text-ink-soft"/>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-surface border border-[#E5E1D8]" align="start">
            <Calendar mode="single" selected={checkOut} onSelect={setCheckOut} defaultMonth={checkOut || checkIn || new Date()} disabled={(d) => d < (checkIn || new Date(new Date().setHours(0,0,0,0)))} initialFocus/>
          </PopoverContent>
        </Popover>
      </div>

      <div className="md:col-span-2">
        <label className="overline text-ink-soft mb-2 block">Room preference</label>
        <Select value={form.room_type} onValueChange={(v) => setForm((f) => ({ ...f, room_type: v }))}>
          <SelectTrigger data-testid="select-room" className="w-full bg-transparent border-0 border-b border-ink/20 rounded-none focus:border-terracotta py-3 text-ink text-sm h-auto">
            <SelectValue placeholder="Pick a room"/>
          </SelectTrigger>
          <SelectContent className="bg-surface border border-[#E5E1D8]">
            {ROOMS.map((r) => (
              <SelectItem key={r.id} value={r.name} data-testid={`select-room-${r.id}`}>
                {r.name} — ₹{r.price.toLocaleString("en-IN")}/night
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="md:col-span-2">
        <label className="overline text-ink-soft mb-2 block">A note for the family (optional)</label>
        <textarea
          data-testid="input-message"
          rows={3}
          value={form.message}
          onChange={update("message")}
          placeholder="Arrival time, dietary preferences, a special request…"
          className={`${inputCls} resize-none`}
        />
      </div>

      <div className="md:col-span-2 flex flex-col md:flex-row md:items-center justify-between gap-4 pt-4 border-t border-[#E5E1D8]">
        <p className="text-xs text-ink-soft leading-relaxed max-w-md">
          A confirmation will reach you at your email. The Bohara Braj Bhushanjee family will reply personally within 24 hours.
          Prefer to talk? WhatsApp <a href="https://wa.me/919783355866" target="_blank" rel="noreferrer" className="underline decoration-terracotta underline-offset-4">+91 97833 55866</a>.
        </p>
        <motion.button
          whileTap={{ scale: 0.97 }}
          type="submit"
          disabled={loading}
          data-testid="btn-submit-booking"
          className="btn-pill btn-primary disabled:opacity-60 disabled:cursor-not-allowed self-start md:self-auto"
        >
          {loading ? "Sending…" : (<>Send enquiry <Send size={14}/></>)}
        </motion.button>
      </div>
    </form>
  );
}
